<template>
  <div class="history-page">
    <header class="history-header">
      <button class="back-btn" @click="$router.push('/dashboard')">
        <font-awesome-icon icon="arrow-left" />
      </button>
      <h1>Temperature History</h1>
    </header>

    <div class="filters">
      <div class="filter-row">
        <select v-model="dateFilter" @change="applyFilters">
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="7days">Last 7 Days</option>
          <option value="30days">Last 30 Days</option>
          <option value="all">All</option>
        </select>

        <select v-model="probeFilter" @change="applyFilters">
          <option value="all">All Probes</option>
          <option v-for="probe in probeTypes" :key="probe.id" :value="probe.id">
            {{ probe.label }}
          </option>
        </select>

        <select v-model="statusFilter" @change="applyFilters">
          <option value="all">Pass &amp; Fail</option>
          <option value="pass">Pass Only</option>
          <option value="fail">Fail Only</option>
        </select>
      </div>
    </div>

    <div class="summary-bar" v-if="filteredReadings.length">
      <span>{{ filteredReadings.length }} reading{{ filteredReadings.length > 1 ? 's' : '' }}</span>
      <span class="fail-count" v-if="failCount">
        <font-awesome-icon icon="triangle-exclamation" /> {{ failCount }} failed
      </span>
    </div>

    <div class="readings-list">
      <div v-if="filteredReadings.length === 0" class="empty-state">
        No readings found for the selected filters.
      </div>

      <template v-for="(group, date) in groupedReadings" :key="date">
        <div class="date-group-header">{{ date }}</div>
        <div
          v-for="reading in group"
          :key="reading.id"
          :class="['reading-row', reading.pass ? 'pass' : 'fail']"
          @click="toggleDetail(reading.id)"
        >
          <div class="row-main">
            <span :class="['status-dot', reading.pass ? 'dot-pass' : 'dot-fail']" />
            <span class="row-time">{{ formatTime(reading.timestamp) }}</span>
            <span class="row-probe">{{ probeLabel(reading.probeType) }}</span>
            <span class="row-temp">{{ reading.temperature }}°C</span>
            <span :class="['row-badge', reading.pass ? 'badge-pass' : 'badge-fail']">
              {{ reading.pass ? 'PASS' : 'FAIL' }}
            </span>
          </div>
          <div class="row-detail" v-if="expandedId === reading.id">
            <div v-if="reading.checkedBy"><strong>Checked by:</strong> {{ reading.checkedBy }}</div>
            <div v-if="reading.notes"><strong>Notes:</strong> {{ reading.notes }}</div>
            <div v-if="!reading.pass && reading.correctiveAction" class="detail-action">
              <strong>Corrective action:</strong> {{ reading.correctiveAction }}
            </div>
            <div class="detail-time">{{ formatDateTime(reading.timestamp) }}</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { api } from "@monolith/helpers";

const PROBE_TYPES = [
  { id: 'chiller', label: 'Chiller', min: 0, max: 5 },
  { id: 'freezer', label: 'Freezer', min: -30, max: -18 },
  { id: 'hot_hold', label: 'Hot Hold', min: 60, max: 100 },
  { id: 'food_probe', label: 'Food Probe', min: 75, max: 100 },
  { id: 'ambient', label: 'Ambient', min: null, max: null },
];

export default {
  name: "TemperatureHistory",
  data() {
    return {
      probeTypes: PROBE_TYPES,
      dateFilter: 'today',
      probeFilter: 'all',
      statusFilter: 'all',
      expandedId: null,
    }
  },
  computed: {
    allReadings() {
      return this.$store.getters.getReadings;
    },
    filteredReadings() {
      const now = new Date();
      return this.allReadings.filter(r => {
        const ts = new Date(r.timestamp);

        // Date filter
        if (this.dateFilter === 'today') {
          if (ts.toDateString() !== now.toDateString()) return false;
        } else if (this.dateFilter === 'yesterday') {
          const yesterday = new Date(now);
          yesterday.setDate(now.getDate() - 1);
          if (ts.toDateString() !== yesterday.toDateString()) return false;
        } else if (this.dateFilter === '7days') {
          const cutoff = new Date(now);
          cutoff.setDate(now.getDate() - 7);
          if (ts < cutoff) return false;
        } else if (this.dateFilter === '30days') {
          const cutoff = new Date(now);
          cutoff.setDate(now.getDate() - 30);
          if (ts < cutoff) return false;
        }

        // Probe filter
        if (this.probeFilter !== 'all' && r.probeType !== this.probeFilter) return false;

        // Status filter
        if (this.statusFilter === 'pass' && !r.pass) return false;
        if (this.statusFilter === 'fail' && r.pass) return false;

        return true;
      });
    },
    failCount() {
      return this.filteredReadings.filter(r => !r.pass).length;
    },
    groupedReadings() {
      const groups = {};
      for (const r of this.filteredReadings) {
        const label = new Date(r.timestamp).toLocaleDateString('en-NZ', {
          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        });
        if (!groups[label]) groups[label] = [];
        groups[label].push(r);
      }
      return groups;
    }
  },
  async created() {
    if (this.allReadings.length === 0) {
      await this.loadReadings();
    }
  },
  methods: {
    async loadReadings() {
      try {
        const readings = await api.get(`/api/temperature`);
        this.$store.dispatch('setReadings', readings);
      } catch {
        // use whatever is in the store
      }
    },
    applyFilters() {
      this.expandedId = null;
    },
    toggleDetail(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    probeLabel(id) {
      const probe = PROBE_TYPES.find(p => p.id === id);
      return probe ? probe.label : id;
    },
    formatTime(ts) {
      return new Date(ts).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
    },
    formatDateTime(ts) {
      return new Date(ts).toLocaleString('en-NZ', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    }
  }
}
</script>

<style scoped lang="scss">
.history-page {
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 2rem;
}

.history-header {
  background-color: var(--banner-colour);
  color: #fff;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  h1 {
    margin: 0;
    font-size: 1.1rem;
  }
}

.back-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.1rem;
  padding: 0.25rem 0.5rem;
  margin-left: 0;
  cursor: pointer;
}

.filters {
  padding: 1rem 1.25rem 0;
}

.filter-row {
  display: flex;
  gap: 0.5rem;

  select {
    flex: 1;
    padding: 0.6rem 0.5rem;
    border-radius: 0.4rem;
    border: 1px solid var(--border-colour);
    font-size: 0.85rem;
    background-color: var(--primary-colour);
    color: var(--text-colour);
    font-family: "Lato";
  }
}

.summary-bar {
  padding: 0.6rem 1.25rem;
  font-size: 0.85rem;
  opacity: 0.7;
  display: flex;
  justify-content: space-between;
}

.fail-count {
  color: var(--fail-colour);
  font-weight: bold;
  opacity: 1;
}

.readings-list {
  padding: 0 1.25rem;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  opacity: 0.5;
  font-size: 0.9rem;
}

.date-group-header {
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  opacity: 0.5;
  padding: 0.75rem 0 0.4rem;
}

.reading-row {
  background-color: var(--secondary-colour);
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  border-left: 4px solid transparent;
  cursor: pointer;

  &.pass { border-left-color: var(--pass-colour); }
  &.fail { border-left-color: var(--fail-colour); }
}

.row-main {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 0.9rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.dot-pass { background-color: var(--pass-colour); }
  &.dot-fail { background-color: var(--fail-colour); }
}

.row-time {
  font-size: 0.85rem;
  opacity: 0.7;
  min-width: 50px;
}

.row-probe {
  flex: 1;
  font-size: 0.9rem;
}

.row-temp {
  font-weight: bold;
  font-size: 1rem;
}

.row-badge {
  font-size: 0.7rem;
  font-weight: bold;
  padding: 0.2rem 0.45rem;
  border-radius: 0.25rem;
  color: #fff;

  &.badge-pass { background-color: var(--pass-colour); }
  &.badge-fail { background-color: var(--fail-colour); }
}

.row-detail {
  padding: 0 0.9rem 0.75rem;
  font-size: 0.85rem;
  border-top: 1px solid var(--border-colour);

  > div { margin-top: 0.35rem; }
}

.detail-action {
  color: var(--fail-colour);
}

.detail-time {
  opacity: 0.5;
  font-size: 0.8rem;
}
</style>
